import { useState, useMemo, useEffect, useRef, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  startOfMonth,
  endOfMonth,
  subMonths,
  format,
  parseISO,
  differenceInDays,
  subDays,
  eachMonthOfInterval,
  endOfDay,
  isSameMonth,
} from "date-fns";
import { ptBR } from "date-fns/locale";
import { supabase } from "@/integrations/supabase/client";
import { useUnifiedAuth } from "@/hooks/useUnifiedAuth";

async function invokeJadlogImpact(
  keycloakToken: string,
  body: Record<string, unknown>,
) {
  const { data, error } = await supabase.functions.invoke("get-jadlog-impact", {
    body,
    headers: { "x-keycloak-token": keycloakToken },
  });
  if (error) throw error;
  return data;
}

// IMPORTANTE: a página /dadosdeimpactojadlog deve SEMPRE operar com o generator_id da Jadlog,
// mesmo para usuários internos/admin, para evitar mistura de dados entre geradores.
const JADLOG_GENERATOR_ID = "79aa465d-fa35-46a8-9d0c-e4603ac6e812";

export interface ImpactFiltersJadlog {
  dateRange: { from: Date; to: Date };
  establishments: string[];
  materials: string[];
  impactType: string;
  destinationType: string;
  wasteType: string;
  groupBy: "month" | "material" | "unit";
  generatorId: string | null;
}

export interface ImpactKPIJadlog {
  id: string;
  label: string;
  value: number;
  unit: string;
  icon: string;
  change?: number;
  tooltip: string;
}

export interface MaterialSummaryJadlog {
  material: string;
  weight: number;
  percentage: number;
  icon: string;
  color: string;
}

export interface EvolutionDataPointJadlog {
  period: string;
  co2: number;
  energia: number;
  material: number;
  aterro: number;
}

export interface ImpactByMaterialJadlog {
  material: string;
  co2: number;
  energy: number;
  recovered: number;
}

export interface UnitRankingJadlog {
  unit: string;
  value: number;
  co2: number;
}

// Interface para dados agregados da RPC get_jadlog_impact_aggregated
interface AggregatedImpactRowJadlog {
  tipo_material: string;
  massa_kg: number;
  saved_carbon: number;
  saved_space: number;
  generated_material: number;
  generated_energy: number;
  generated_fertilizer: number;
}

// Interface para dados brutos da view (para evolução mensal)
interface ImpactMetricRowJadlog {
  planned_date: string;
  generator_id: string;
  tipo_material: string;
  massa_kg: number;
  saved_carbon: number;
  saved_space: number;
  generated_material: number;
  generated_energy: number;
  generated_fertilizer: number;
}

// Interface para materiais "Sem Regra"
export interface SemRegraMaterialJadlog {
  kind_of_material: string;
  translated_name: string;
  massa_kg: number;
}

// Removido: generateAvailableMonths estático — agora os meses disponíveis vêm da tabela jadlog_impact_monthly.

const getDefaultFiltersJadlog = (): ImpactFiltersJadlog => {
  // Padrão: últimos 6 meses (excluindo mês atual)
  const currentMonth = startOfMonth(new Date());
  const sixMonthsAgo = subMonths(currentMonth, 6);
  const previousMonth = subMonths(currentMonth, 1);

  return {
    dateRange: {
      from: startOfMonth(sixMonthsAgo),
      to: endOfMonth(previousMonth),
    },
    establishments: [],
    materials: [],
    impactType: "all",
    destinationType: "all",
    wasteType: "all",
    groupBy: "month",
    generatorId: JADLOG_GENERATOR_ID,
  };
};

// Configuração dos materiais Jadlog - DERIVADA DA VIEW
const MATERIAL_CONFIG_JADLOG: Record<
  string,
  { label: string; icon: string; color: string }
> = {
  Papelão: { label: "Papelão", icon: "package", color: "#8B5CF6" },
  "Recicláveis Mistos": {
    label: "Recicláveis Mistos",
    icon: "recycle",
    color: "#10B981",
  },
  Orgânico: { label: "Orgânico", icon: "leaf", color: "#22C55E" },
  Rejeito: { label: "Rejeito", icon: "trash", color: "#EAB308" },
  Madeira: { label: "Madeira", icon: "tree", color: "#A16207" },
  "Sem Regra": {
    label: "Sem Regra",
    icon: "more-horizontal",
    color: "#9CA3AF",
  },
};

const getMaterialConfigJadlog = (material: string) => {
  return (
    MATERIAL_CONFIG_JADLOG[material] || {
      label: material,
      icon: "box",
      color: "#9CA3AF",
    }
  );
};

export const useImpactDataJadlog = () => {
  const { profile, keycloakToken } = useUnifiedAuth();
  const hasInitialized = useRef(false);

  // Query dinâmica: buscar meses disponíveis na tabela jadlog_impact_monthly
  const { data: availableMonthsRaw } = useQuery({
    queryKey: ['jadlog-available-months'],
    queryFn: async () => {
      const data = await invokeJadlogImpact(keycloakToken!, { action: "available-months" });
      const uniqueDates = [...new Set((data || []).map((r: any) => r.reference_date as string))];
      return uniqueDates.map((d) => parseISO(d as string));
    },
    staleTime: 10 * 60 * 1000,
    enabled: !!profile && !!keycloakToken,
  });

  const availableMonths = useMemo(() => availableMonthsRaw || [], [availableMonthsRaw]);

  // Último mês disponível
  const lastAvailableMonth = useMemo(() => {
    return availableMonths.length > 0
      ? availableMonths[availableMonths.length - 1]
      : null;
  }, [availableMonths]);

  const [filters, setFilters] = useState<ImpactFiltersJadlog>(() =>
    getDefaultFiltersJadlog(),
  );

  // Quando os meses disponíveis carregarem, ajustar o filtro default para os últimos 6 meses disponíveis
  useEffect(() => {
    if (profile && availableMonths.length > 0 && !hasInitialized.current) {
      const lastN = availableMonths.slice(-6);
      const from = startOfMonth(lastN[0]);
      const to = endOfMonth(lastN[lastN.length - 1]);

      setFilters((prev) => ({
        ...prev,
        dateRange: { from, to },
      }));
      hasInitialized.current = true;
    }
  }, [profile, availableMonths]);

  // Validar se o período não inclui o mês atual
  const validateDateRange = useCallback(
    (from: Date, to: Date): boolean => {
      const currentMonth = startOfMonth(new Date());
      const fromMonth = startOfMonth(from);
      const toMonth = startOfMonth(to);

      // Não permitir mês atual
      if (
        isSameMonth(fromMonth, currentMonth) ||
        isSameMonth(toMonth, currentMonth)
      ) {
        return false;
      }

      // Verificar se os meses estão dentro do range disponível (últimos 24 meses)
      const monthsInRange = eachMonthOfInterval({
        start: fromMonth,
        end: toMonth,
      });

      return monthsInRange.every((month) => {
        if (isSameMonth(month, currentMonth)) {
          return false;
        }
        return availableMonths.some((availableMonth) =>
          isSameMonth(availableMonth, month),
        );
      });
    },
    [availableMonths],
  );

  const previousPeriod = useMemo(() => {
    const daysDiff = differenceInDays(
      filters.dateRange.to,
      filters.dateRange.from,
    );
    return {
      from: subDays(filters.dateRange.from, daysDiff + 1),
      to: subDays(filters.dateRange.from, 1),
    };
  }, [filters.dateRange]);

  // Parâmetros de query derivados dos filtros (evita recálculo durante render)
  const queryParams = useMemo(
    () => ({
      startDate: format(filters.dateRange.from, "yyyy-MM-dd"),
      endDate: format(filters.dateRange.to, "yyyy-MM-dd"),
      generatorId: JADLOG_GENERATOR_ID,
      materialFilter:
        filters.wasteType && filters.wasteType !== "all"
          ? filters.wasteType
          : null,
    }),
    [filters.dateRange.from, filters.dateRange.to, filters.wasteType],
  );

  const previousQueryParams = useMemo(
    () => ({
      startDate: format(previousPeriod.from, "yyyy-MM-dd"),
      endDate: format(previousPeriod.to, "yyyy-MM-dd"),
      generatorId: JADLOG_GENERATOR_ID,
      materialFilter:
        filters.wasteType && filters.wasteType !== "all"
          ? filters.wasteType
          : null,
    }),
    [previousPeriod.from, previousPeriod.to, filters.wasteType],
  );

  // Query principal usando a edge function get-jadlog-impact (dados já agregados)
  const { data: aggregatedData, isLoading: isLoadingAggregated } = useQuery({
    queryKey: [
      "jadlog-impact-aggregated",
      queryParams.startDate,
      queryParams.endDate,
      queryParams.materialFilter,
      queryParams.generatorId,
    ],
    queryFn: async () => {
      const data = await invokeJadlogImpact(keycloakToken!, {
        action: "aggregated",
        p_start_date: queryParams.startDate,
        p_end_date: queryParams.endDate,
        p_material: queryParams.materialFilter,
      });
      return (data || []) as AggregatedImpactRowJadlog[];
    },
    staleTime: 1 * 60 * 1000,
    enabled: !!profile && !!keycloakToken,
  });

  // Query para período anterior (para cálculo de variação)
  const { data: previousAggregatedData } = useQuery({
    queryKey: [
      "jadlog-impact-aggregated-previous",
      previousQueryParams.startDate,
      previousQueryParams.endDate,
      previousQueryParams.materialFilter,
      previousQueryParams.generatorId,
    ],
    queryFn: async () => {
      const data = await invokeJadlogImpact(keycloakToken!, {
        action: "aggregated",
        p_start_date: previousQueryParams.startDate,
        p_end_date: previousQueryParams.endDate,
        p_material: previousQueryParams.materialFilter,
      });
      return (data || []) as AggregatedImpactRowJadlog[];
    },
    staleTime: 5 * 60 * 1000,
    enabled: !!profile && !!keycloakToken,
  });

  // Query para dados brutos (necessário para evolução mensal)
  const { data: rawData, isLoading: isLoadingRaw } = useQuery({
    queryKey: [
      "jadlog-impact-raw",
      queryParams.startDate,
      queryParams.endDate,
      queryParams.materialFilter,
      queryParams.generatorId,
    ],
    queryFn: async () => {
      const data = await invokeJadlogImpact(keycloakToken!, {
        action: "raw-metrics",
        p_start_date: queryParams.startDate,
        p_end_date: format(endOfDay(filters.dateRange.to), "yyyy-MM-dd HH:mm:ss"),
        p_material: queryParams.materialFilter,
      });
      return (data || []) as ImpactMetricRowJadlog[];
    },
    staleTime: 1 * 60 * 1000,
    enabled: !!profile && !!keycloakToken,
  });

  // Query para buscar materiais que compõem "Sem Regra"
  const { data: semRegraMaterials } = useQuery({
    queryKey: [
      "jadlog-sem-regra-materials",
      queryParams.startDate,
      queryParams.endDate,
      queryParams.generatorId,
    ],
    queryFn: async () => {
      const data = await invokeJadlogImpact(keycloakToken!, {
        action: "sem-regra",
        p_start_date: queryParams.startDate,
        p_end_date: queryParams.endDate,
      });
      return (data || []) as SemRegraMaterialJadlog[];
    },
    staleTime: 5 * 60 * 1000,
    enabled: !!profile && !!keycloakToken,
  });

  const isLoading = isLoadingAggregated || isLoadingRaw;

  const calculateChange = (
    current: number,
    previous: number,
  ): number | undefined => {
    if (previous === 0) return current > 0 ? 100 : undefined;
    return Math.round(((current - previous) / previous) * 100);
  };

  // Opções de material derivadas dos dados agregados
  const materialOptionsFromData = useMemo(() => {
    if (!aggregatedData || aggregatedData.length === 0) {
      return [];
    }

    return aggregatedData
      .filter((row) => row.tipo_material !== "TOTAL" && row.massa_kg > 0)
      .map((row) => ({
        value: row.tipo_material,
        label: getMaterialConfigJadlog(row.tipo_material).label,
      }))
      .sort((a, b) => {
        const order = [
          "Papelão",
          "Recicláveis Mistos",
          "Orgânico",
          "Rejeito",
          "Madeira",
          "Sem Regra",
        ];
        return order.indexOf(a.value) - order.indexOf(b.value);
      });
  }, [aggregatedData]);

  const data = useMemo(() => {
    try {
      if (!aggregatedData || aggregatedData.length === 0) {
        return {
          totalWeight: 0,
          materialSummary: [],
          kpis: [],
          evolutionData: [],
          impactByMaterial: [],
          unitRanking: [],
        };
      }

      // Encontrar a linha TOTAL
      const totalRow = aggregatedData.find(
        (row) => row.tipo_material === "TOTAL",
      );
      const materialRows = aggregatedData.filter(
        (row) => row.tipo_material !== "TOTAL",
      );

      // Totais do período atual (direto da RPC)
      const totals = {
        massa_kg: Number(totalRow?.massa_kg || 0),
        saved_carbon: Number(totalRow?.saved_carbon || 0),
        saved_space: Number(totalRow?.saved_space || 0),
        generated_material: Number(totalRow?.generated_material || 0),
        generated_energy: Number(totalRow?.generated_energy || 0),
        generated_fertilizer: Number(totalRow?.generated_fertilizer || 0),
      };

      // Totais do período anterior
      const previousTotalRow = (previousAggregatedData || []).find(
        (row) => row.tipo_material === "TOTAL",
      );
      const previousTotals = {
        massa_kg: Number(previousTotalRow?.massa_kg || 0),
        saved_carbon: Number(previousTotalRow?.saved_carbon || 0),
        saved_space: Number(previousTotalRow?.saved_space || 0),
        generated_material: Number(previousTotalRow?.generated_material || 0),
        generated_energy: Number(previousTotalRow?.generated_energy || 0),
        generated_fertilizer: Number(
          previousTotalRow?.generated_fertilizer || 0,
        ),
      };

      // KPIs com nomenclatura Jadlog
      const kpis: ImpactKPIJadlog[] = [
        {
          id: "co2",
          label: "Carbono Evitado",
          value: Math.round(totals.saved_carbon * 100) / 100,
          unit: "kg",
          icon: "cloud",
          change: calculateChange(
            totals.saved_carbon,
            previousTotals.saved_carbon,
          ),
          tooltip:
            "Carbono evitado calculado com base nas fórmulas de impacto por tipo de material.",
        },
        {
          id: "landfill",
          label: "Espaço Poupado em Aterro",
          value: Math.round(totals.saved_space * 1000) / 1000,
          unit: "m³",
          icon: "trash",
          change: calculateChange(
            totals.saved_space,
            previousTotals.saved_space,
          ),
          tooltip:
            "Volume de aterro sanitário evitado com a destinação correta.",
        },
        {
          id: "recovered",
          label: "Material Gerado",
          value: Math.round(totals.generated_material * 100) / 100,
          unit: "kg",
          icon: "recycle",
          change: calculateChange(
            totals.generated_material,
            previousTotals.generated_material,
          ),
          tooltip: "Material efetivamente recuperado para reciclagem.",
        },
        {
          id: "energy",
          label: "Energia Gerada",
          value: Math.round(totals.generated_energy * 100) / 100,
          unit: "kWh",
          icon: "zap",
          change: calculateChange(
            totals.generated_energy,
            previousTotals.generated_energy,
          ),
          tooltip: "Energia recuperada dos resíduos.",
        },
      ];

      // Adicionar Fertilizante apenas se > 0
      if (totals.generated_fertilizer > 0) {
        kpis.push({
          id: "fertilizer",
          label: "Fertilizante Gerado",
          value: Math.round(totals.generated_fertilizer * 100) / 100,
          unit: "kg",
          icon: "sprout",
          change: calculateChange(
            totals.generated_fertilizer,
            previousTotals.generated_fertilizer,
          ),
          tooltip: "Fertilizante gerado a partir de resíduos orgânicos.",
        });
      }

      // Material Summary - direto das linhas agregadas
      const materialSummary: MaterialSummaryJadlog[] = materialRows
        .filter((row) => row.massa_kg > 0)
        .map((row) => {
          const config = getMaterialConfigJadlog(row.tipo_material);
          return {
            material: config.label,
            weight: Number(row.massa_kg),
            percentage:
              totals.massa_kg > 0
                ? (Number(row.massa_kg) / totals.massa_kg) * 100
                : 0,
            icon: config.icon,
            color: config.color,
          };
        })
        .sort((a, b) => b.weight - a.weight);

      // Impact by Material - para gráficos
      // Quando filtro TOTAL: mesclar Papelão em Recicláveis Mistos
      const shouldMergePapelao = filters.wasteType === "all";
      const materialMapCharts = new Map<
        string,
        { massa: number; co2: number; energia: number; recuperado: number }
      >();

      materialRows.forEach((row) => {
        let material = row.tipo_material;

        if (shouldMergePapelao && material === "Papelão") {
          material = "Recicláveis Mistos";
        }

        const current = materialMapCharts.get(material) || {
          massa: 0,
          co2: 0,
          energia: 0,
          recuperado: 0,
        };
        materialMapCharts.set(material, {
          massa: current.massa + Number(row.massa_kg || 0),
          co2: current.co2 + Number(row.saved_carbon || 0),
          energia: current.energia + Number(row.generated_energy || 0),
          recuperado: current.recuperado + Number(row.generated_material || 0),
        });
      });

      const impactByMaterial: ImpactByMaterialJadlog[] = Array.from(
        materialMapCharts.entries(),
      )
        .filter(([_, data]) => data.massa > 0)
        .map(([material, data]) => {
          const config = getMaterialConfigJadlog(material);
          return {
            material: config.label,
            co2: Math.round(data.co2),
            energy: Math.round(data.energia),
            recovered: Math.round(data.recuperado),
          };
        })
        .sort((a, b) => b.co2 - a.co2);

      // Evolução mensal (usa dados brutos)
      const monthMap = new Map<
        string,
        { co2: number; energia: number; material: number; aterro: number }
      >();
      (rawData || []).forEach((row) => {
        const date = parseISO(row.planned_date);
        const monthKey = format(date, "yyyy-MM");
        const current = monthMap.get(monthKey) || {
          co2: 0,
          energia: 0,
          material: 0,
          aterro: 0,
        };
        monthMap.set(monthKey, {
          co2: current.co2 + Number(row.saved_carbon || 0),
          energia: current.energia + Number(row.generated_energy || 0),
          material: current.material + Number(row.generated_material || 0),
          aterro: current.aterro + Number(row.saved_space || 0),
        });
      });

      const evolutionData: EvolutionDataPointJadlog[] = [];
      const monthsInRange = eachMonthOfInterval({
        start: startOfMonth(filters.dateRange.from),
        end: endOfMonth(filters.dateRange.to),
      });

      monthsInRange.forEach((date) => {
        const monthKey = format(date, "yyyy-MM");
        const monthLabel = format(date, "MMM", { locale: ptBR });
        const data = monthMap.get(monthKey) || {
          co2: 0,
          energia: 0,
          material: 0,
          aterro: 0,
        };

        const hasData =
          data.co2 > 0 ||
          data.energia > 0 ||
          data.material > 0 ||
          data.aterro > 0;

        if (hasData) {
          evolutionData.push({
            period: monthLabel.charAt(0).toUpperCase() + monthLabel.slice(1),
            co2: Math.round(data.co2),
            energia: Math.round(data.energia),
            material: Math.round(data.material),
            aterro: Math.round(data.aterro * 1000) / 1000,
          });
        }
      });

      // Ranking por material - usa dados agregados
      const unitRanking: UnitRankingJadlog[] = Array.from(
        materialMapCharts.entries(),
      )
        .map(([material, data]) => ({
          unit: getMaterialConfigJadlog(material).label,
          value: data.massa,
          co2: data.co2,
        }))
        .sort((a, b) => b.value - a.value)
        .slice(0, 10);

      return {
        totalWeight: totals.massa_kg,
        materialSummary,
        kpis,
        evolutionData,
        impactByMaterial,
        unitRanking,
      };
    } catch (error) {
      console.error("❌ Error processing impact data:", error);
      return {
        totalWeight: 0,
        materialSummary: [],
        kpis: [],
        evolutionData: [],
        impactByMaterial: [],
        unitRanking: [],
      };
    }
  }, [
    aggregatedData,
    previousAggregatedData,
    rawData,
    filters.dateRange,
    filters.wasteType,
  ]);

  const dateShortcuts = useMemo(() => {
    if (availableMonths.length === 0) {
      return [];
    }

    const shortcuts = [];

    const lastMonth = availableMonths[availableMonths.length - 1];
    if (lastMonth) {
      shortcuts.push({
        label: "Mês atual",
        getValue: () => ({
          from: startOfMonth(lastMonth),
          to: endOfMonth(lastMonth),
        }),
      });
    }

    if (availableMonths.length >= 3) {
      const last3Months = availableMonths.slice(-3);
      shortcuts.push({
        label: "Últimos 3 meses",
        getValue: () => {
          const from = startOfMonth(last3Months[0]);
          const to = endOfMonth(last3Months[last3Months.length - 1]);
          return { from, to };
        },
      });
    }

    if (availableMonths.length >= 6) {
      const last6Months = availableMonths.slice(-6);
      shortcuts.push({
        label: "Últimos 6 meses",
        getValue: () => {
          const from = startOfMonth(last6Months[0]);
          const to = endOfMonth(last6Months[last6Months.length - 1]);
          return { from, to };
        },
      });
    }

    return shortcuts;
  }, [availableMonths]);

  const updateFilters = (newFilters: Partial<ImpactFiltersJadlog>) => {
    setFilters((prev) => ({ ...prev, ...newFilters }));
  };

  const resetFilters = () => {
    if (availableMonths.length > 0) {
      const lastN = availableMonths.slice(-6);
      setFilters({
        ...getDefaultFiltersJadlog(),
        dateRange: {
          from: startOfMonth(lastN[0]),
          to: endOfMonth(lastN[lastN.length - 1]),
        },
      });
    } else {
      setFilters(getDefaultFiltersJadlog());
    }
  };

  const establishmentOptions: { value: string; label: string }[] = [];

  // Opções de material dinâmicas - derivadas da view
  const wasteTypeOptions = materialOptionsFromData;

  return {
    filters,
    updateFilters,
    resetFilters,
    dateShortcuts,
    data,
    isLoading,
    establishmentOptions,
    materialOptions: materialOptionsFromData,
    wasteTypeOptions,
    closedMonths: availableMonths,
    validateDateRange,
    semRegraMaterials: semRegraMaterials || [],
  };
};
